import React, { useEffect, useState } from 'react';
import { BarChart3, Smartphone, Monitor, Tablet, TrendingUp, Users, RefreshCw, MousePointer, Eye, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { analyticsService, portfolioService, recentConsultationsService } from '@/lib/api';
import { GestionMetrics } from '@/components/analytics/GestionMetrics';

const CompanyAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recentConsultations, setRecentConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [portfolioFilter, setPortfolioFilter] = useState('all');
  const [portfolios, setPortfolios] = useState([]);

  useEffect(() => {
    loadPortfolios();
  }, []);

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, portfolioFilter]);

  const loadPortfolios = async () => {
    try {
      const response = await portfolioService.list();
      setPortfolios(response.data);
    } catch (error) {
      console.error('Error loading portfolios:', error);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const portfolioId = portfolioFilter === 'all' ? null : portfolioFilter;
      const [consultations, summaryData, recentRes] = await Promise.all([
        analyticsService.getCompanyConsultations(days, portfolioId),
        analyticsService.getCompanySummary(),
        recentConsultationsService.list({ limit: 30, portfolio_id: portfolioId || undefined })
      ]);
      setAnalyticsData(consultations.data);
      setSummary(summaryData.data);
      setRecentConsultations(recentRes.data || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Error al cargar analytics');
    } finally {
      setLoading(false);
    }
  };

  const sectionLabels = {
    'pagar': 'Pagar Ahora',
    'reportar_pago': 'Reportar Pago',
    'libre_deuda': 'Libre Deuda',
    'solicitudes': 'Solicitudes',
    'mis_datos': 'Mis Datos',
    'inicio': 'Inicio',
    'unknown': 'Desconocido'
  };

  const sectionColors = {
    'pagar': 'bg-emerald-500',
    'reportar_pago': 'bg-blue-500',
    'libre_deuda': 'bg-violet-500',
    'solicitudes': 'bg-amber-500',
    'mis_datos': 'bg-slate-500',
    'inicio': 'bg-teal-500',
    'unknown': 'bg-gray-400'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Analytics de Clientes</h2>
          <p className="text-slate-600">Comportamiento y consultas en el portal</p>
        </div>
        
        <div className="flex gap-3 items-center flex-wrap">
          <Select value={portfolioFilter} onValueChange={setPortfolioFilter}>
            <SelectTrigger className="w-[200px]" data-testid="analytics-portfolio-filter">
              <SelectValue placeholder="Todas las carteras" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las carteras</SelectItem>
              {portfolios.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-[160px]" data-testid="analytics-days-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            onClick={loadAnalytics}
            disabled={loading}
            variant="outline"
            data-testid="analytics-refresh-btn"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Hoy</p>
              <p className="text-2xl font-bold text-slate-900">
                {summary?.today?.toLocaleString('es-AR') || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-teal-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Semana</p>
              <p className="text-2xl font-bold text-slate-900">
                {summary?.this_week?.toLocaleString('es-AR') || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Mes</p>
              <p className="text-2xl font-bold text-slate-900">
                {summary?.this_month?.toLocaleString('es-AR') || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">% Móvil</p>
              <p className="text-2xl font-bold text-slate-900">
                {summary?.mobile_percentage || 0}%
              </p>
            </div>
            <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-violet-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Acciones</p>
              <p className="text-2xl font-bold text-slate-900">
                {summary?.total_actions?.toLocaleString('es-AR') || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <MousePointer className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </Card>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <RefreshCw className="w-8 h-8 text-slate-400 mx-auto animate-spin" />
          <p className="text-slate-600 mt-2">Cargando analytics...</p>
        </div>
      ) : analyticsData ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Device Breakdown */}
          <Card className="p-6 border-slate-200 bg-white">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              Consultas por Dispositivo
            </h3>
            <div className="space-y-4">
              {/* Mobile */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-violet-600" />
                    Móvil
                  </span>
                  <span className="font-medium">
                    {analyticsData.device_breakdown?.mobile || 0}
                    <span className="text-slate-500 ml-1">
                      ({analyticsData.total_consultations > 0 
                        ? Math.round((analyticsData.device_breakdown?.mobile || 0) / analyticsData.total_consultations * 100) 
                        : 0}%)
                    </span>
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div 
                    className="bg-violet-600 h-3 rounded-full transition-all"
                    style={{ 
                      width: `${analyticsData.total_consultations > 0 
                        ? (analyticsData.device_breakdown?.mobile || 0) / analyticsData.total_consultations * 100 
                        : 0}%` 
                    }}
                  />
                </div>
              </div>
              
              {/* Desktop */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-blue-600" />
                    Desktop
                  </span>
                  <span className="font-medium">
                    {analyticsData.device_breakdown?.desktop || 0}
                    <span className="text-slate-500 ml-1">
                      ({analyticsData.total_consultations > 0 
                        ? Math.round((analyticsData.device_breakdown?.desktop || 0) / analyticsData.total_consultations * 100) 
                        : 0}%)
                    </span>
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all"
                    style={{ 
                      width: `${analyticsData.total_consultations > 0 
                        ? (analyticsData.device_breakdown?.desktop || 0) / analyticsData.total_consultations * 100 
                        : 0}%` 
                    }}
                  />
                </div>
              </div>
              
              {/* Tablet */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Tablet className="w-4 h-4 text-teal-600" />
                    Tablet
                  </span>
                  <span className="font-medium">
                    {analyticsData.device_breakdown?.tablet || 0}
                    <span className="text-slate-500 ml-1">
                      ({analyticsData.total_consultations > 0 
                        ? Math.round((analyticsData.device_breakdown?.tablet || 0) / analyticsData.total_consultations * 100) 
                        : 0}%)
                    </span>
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div 
                    className="bg-teal-600 h-3 rounded-full transition-all"
                    style={{ 
                      width: `${analyticsData.total_consultations > 0 
                        ? (analyticsData.device_breakdown?.tablet || 0) / analyticsData.total_consultations * 100 
                        : 0}%` 
                    }}
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-100 text-center">
              <p className="text-3xl font-bold text-slate-900">{analyticsData.total_consultations?.toLocaleString('es-AR')}</p>
              <p className="text-sm text-slate-600">consultas totales</p>
            </div>
          </Card>

          {/* Section Activity - Behavior Tracking */}
          <Card className="p-6 border-slate-200 bg-white">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Actividad por Sección
            </h3>
            {analyticsData.section_activity?.length > 0 ? (
              <div className="space-y-3">
                {analyticsData.section_activity.map(({ section, count }) => {
                  const totalActions = analyticsData.total_actions || 1;
                  const percentage = Math.round(count / totalActions * 100);
                  return (
                    <div key={section} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{sectionLabels[section] || section}</span>
                        <span className="text-slate-600">{count} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2.5">
                        <div 
                          className={`${sectionColors[section] || 'bg-slate-400'} h-2.5 rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <MousePointer className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p>No hay datos de navegación aún</p>
                <p className="text-sm mt-1">Los datos se registran cuando los clientes navegan en el portal</p>
              </div>
            )}
            
            {summary?.top_section && summary.top_section !== 'N/A' && (
              <div className="mt-6 pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-600">Sección más visitada</p>
                <Badge className="mt-1 bg-emerald-100 text-emerald-700">
                  {sectionLabels[summary.top_section] || summary.top_section}
                </Badge>
              </div>
            )}
          </Card>

          {/* OS Breakdown */}
          <Card className="p-6 border-slate-200 bg-white">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Sistema Operativo
            </h3>
            <div className="space-y-3">
              {Object.entries(analyticsData.os_breakdown || {})
                .sort((a, b) => b[1] - a[1])
                .map(([os, count]) => {
                  const percentage = analyticsData.total_consultations > 0 
                    ? Math.round(count / analyticsData.total_consultations * 100) 
                    : 0;
                  const colors = {
                    'Android': 'bg-green-500',
                    'iOS': 'bg-slate-800',
                    'Windows': 'bg-blue-500',
                    'macOS': 'bg-slate-600',
                    'Linux': 'bg-orange-500',
                    'ChromeOS': 'bg-yellow-500',
                    'unknown': 'bg-slate-300'
                  };
                  return (
                    <div key={os} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{os}</span>
                        <span className="text-slate-600">{count} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2.5">
                        <div 
                          className={`${colors[os] || 'bg-slate-400'} h-2.5 rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </Card>

          {/* Portfolios Breakdown */}
          <Card className="p-6 border-slate-200 bg-white">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Consultas por Cartera
            </h3>
            {analyticsData.portfolios?.length > 0 ? (
              <div className="space-y-3">
                {analyticsData.portfolios.map((portfolio) => (
                  <div key={portfolio.name || 'default'} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{portfolio.name || 'Sin cartera'}</p>
                      <p className="text-sm text-slate-600">
                        {portfolio.mobile || 0} móvil • {portfolio.desktop || 0} desktop
                      </p>
                    </div>
                    <Badge className="bg-teal-100 text-teal-700">
                      {portfolio.total} consultas
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p>No hay datos por cartera aún</p>
              </div>
            )}
          </Card>

          {/* Recent Consultations */}
          <Card className="p-6 border-slate-200 bg-white lg:col-span-2" data-testid="recent-consultations-card">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Consultas Recientes de Clientes
            </h3>
            {recentConsultations.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">Cliente</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">DNI</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">Cartera</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">Deuda</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">Dispositivo</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">Fecha</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {recentConsultations.map((c) => (
                      <tr key={c.id || `${c.debtor_dni}-${c.consulted_at}`} className="hover:bg-slate-50" data-testid={`consultation-row-${c.id || c.debtor_dni}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-900">{c.debtor_name}</td>
                        <td className="px-4 py-2.5 text-slate-600 font-mono text-xs">{c.debtor_dni || '-'}</td>
                        <td className="px-4 py-2.5">
                          <Badge variant="outline" className="text-xs">{c.portfolio_name}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-slate-900 font-medium">${(c.total_debt || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1 text-xs">
                            {c.device === 'mobile' ? <Smartphone className="w-3 h-3 text-violet-600" /> : <Monitor className="w-3 h-3 text-blue-600" />}
                            {c.os}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">
                          {new Date(c.consulted_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p>No hay consultas recientes</p>
              </div>
            )}
          </Card>
        </div>
      ) : (
        <div className="text-center py-12">
          <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            No hay datos de analytics
          </h3>
          <p className="text-slate-600">
            Los datos se generarán cuando los clientes consulten sus deudas.
          </p>
        </div>
      )}

      <hr className="border-slate-200" />
      <GestionMetrics portfolioId={portfolioFilter === 'all' ? null : portfolioFilter} />
    </div>
  );
};

export default CompanyAnalytics;
